import React, { useContext, useState } from 'react'
import { Menu, MenuTrigger, Button, MenuPopover, MenuList, MenuItem, Text, Tooltip, makeStyles, tokens } from '@fluentui/react-components'
import { MoreVerticalRegular } from '@fluentui/react-icons'
import { DataContext } from '../../../context/DataContext'
import { t } from '../../../i18n/i18n'
import { IDataContext } from '../../../interfaces/IDataContext'
import { Modal, ModalActions } from '../modal/Modal'
import { Loader } from '../spinner/Loader'

const useStyles = makeStyles({
   magrginTop: {
      marginTop: tokens.spacingVerticalXL,
   },
   loaderContainer: {
      height: '50px',
   },
})

// An extra menu item rendered above the delete item, with its own confirmation dialog.
export type MenuAction = {
   label: string
   onAction: (item: any, dataContext: IDataContext) => Promise<void>
   confirmationMessage: string
   // Defaults to the label when left out.
   title?: string
   disabled?: boolean
   disabledToolTipMessage?: string
   // Closes whatever the menu sits in once the action has run, mirroring closeOnDelete. Without it a
   // modal around the menu would keep showing the item in its pre-action state.
   closeOnAction?: () => void
}

type MoreOptionsMenuButtonProps = {
   item: any
   setState: React.Dispatch<React.SetStateAction<any>>
   deleteFunction: (id: string, dataContext: IDataContext, setState: React.Dispatch<React.SetStateAction<any>>) => Promise<void>
   deleteMessage: string
   deleteConfirmationMessage: string
   disabled?: boolean
   disabledToolTipMessage?: string
   closeOnDelete?: () => void
   // Left out by every call site that only needs the delete action.
   actions?: MenuAction[]
}

export const MoreOptionsMenuButton = ({
   item,
   setState,
   deleteFunction,
   deleteMessage,
   deleteConfirmationMessage,
   disabled,
   disabledToolTipMessage,
   closeOnDelete,
   actions,
}: MoreOptionsMenuButtonProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const [openModal, setOpenModal] = useState(false)
   const [loading, setLoading] = useState(false)
   // Index into actions, so only one confirmation dialog can be open at a time.
   const [openActionIndex, setOpenActionIndex] = useState<number | null>(null)
   const [actionLoading, setActionLoading] = useState(false)

   const openAction = openActionIndex !== null ? actions?.[openActionIndex] : undefined

   const handleDelete = async (e: any) => {
      e.stopPropagation()
      setLoading(true)
      try {
         await deleteFunction(item.id, dataContext, setState)
         setOpenModal(false)
         closeOnDelete?.()
      } finally {
         setLoading(false)
      }
   }

   const handleAction = async (e: any) => {
      e.stopPropagation()
      if (!openAction) return
      setActionLoading(true)
      try {
         await openAction.onAction(item, dataContext)
         setOpenActionIndex(null)
         openAction.closeOnAction?.()
      } finally {
         setActionLoading(false)
      }
   }

   const closeModal = () => {
      setOpenModal(false)
   }

   const closeActionModal = () => {
      setOpenActionIndex(null)
   }

   return (
      <>
         <Menu positioning="below-start">
            <MenuTrigger disableButtonEnhancement>
               <Button style={{ marginLeft: 'auto' }} appearance="transparent" icon={<MoreVerticalRegular />} onClick={(e: any) => e.stopPropagation()} />
            </MenuTrigger>
            <MenuPopover>
               <MenuList>
                  {actions?.map((action, index) =>
                     action.disabled && action.disabledToolTipMessage ? (
                        <Tooltip key={action.label} showDelay={500} content={action.disabledToolTipMessage} relationship="label">
                           <MenuItem disabled>{action.label}</MenuItem>
                        </Tooltip>
                     ) : (
                        <MenuItem
                           key={action.label}
                           disabled={action.disabled}
                           onClick={(e: any) => {
                              e.stopPropagation()
                              setOpenActionIndex(index)
                           }}
                        >
                           {action.label}
                        </MenuItem>
                     )
                  )}
                  {disabled && disabledToolTipMessage ? (
                     <Tooltip showDelay={500} content={disabledToolTipMessage} relationship="label">
                        <MenuItem disabled={disabled}>{t('Delete')}</MenuItem>
                     </Tooltip>
                  ) : (
                     <MenuItem
                        onClick={(e: any) => {
                           e.stopPropagation()
                           setOpenModal(true)
                        }}
                     >
                        {t('Delete')}
                     </MenuItem>
                  )}
               </MenuList>
            </MenuPopover>
         </Menu>

         <Modal open={openModal} title={deleteMessage}>
            {loading ? (
               <div className={classes.loaderContainer}>
                  <Loader />
               </div>
            ) : (
               <Text align="center">{deleteConfirmationMessage}</Text>
            )}
            <ModalActions className={classes.magrginTop}>
               {loading ? null : (
                  <>
                     <Button onClick={closeModal}>{t('Cancel')}</Button>
                     <Button appearance="primary" onClick={handleDelete}>
                        {t('Delete')}
                     </Button>
                  </>
               )}
            </ModalActions>
         </Modal>

         {openAction && (
            <Modal open title={openAction.title ?? openAction.label}>
               {actionLoading ? (
                  <div className={classes.loaderContainer}>
                     <Loader />
                  </div>
               ) : (
                  <Text align="center">{openAction.confirmationMessage}</Text>
               )}
               <ModalActions className={classes.magrginTop}>
                  {actionLoading ? null : (
                     <>
                        <Button onClick={closeActionModal}>{t('Cancel')}</Button>
                        <Button appearance="primary" onClick={handleAction}>
                           {openAction.label}
                        </Button>
                     </>
                  )}
               </ModalActions>
            </Modal>
         )}
      </>
   )
}
